import { ref, type Ref } from 'vue'

export function useDebounce<T extends unknown[]>(
  fn: (...args: T) => unknown,
  delay = 300,
) {
  const timer = ref<ReturnType<typeof setTimeout> | null>(null)

  const debounced = (...args: T) => {
    if (timer.value) clearTimeout(timer.value)
    timer.value = setTimeout(() => fn(...args), delay)
  }

  const cancel = () => {
    if (timer.value) {
      clearTimeout(timer.value)
      timer.value = null
    }
  }

  const flush = (...args: T) => {
    cancel()
    fn(...args)
  }

  return { debounced, cancel, flush }
}

export function useDebouncedRef<T>(initialValue: T, delay = 300) {
  const value: Ref<T> = ref(initialValue) as Ref<T>
  const debouncedValue: Ref<T> = ref(initialValue) as Ref<T>
  const timer = ref<ReturnType<typeof setTimeout> | null>(null)

  const update = (newVal: T) => {
    value.value = newVal
    if (timer.value) clearTimeout(timer.value)
    timer.value = setTimeout(() => {
      debouncedValue.value = newVal
    }, delay)
  }

  const cancel = () => {
    if (timer.value) {
      clearTimeout(timer.value)
      timer.value = null
    }
  }

  return { value, debouncedValue, update, cancel }
}

export function useThrottle<T extends unknown[]>(
  fn: (...args: T) => unknown,
  interval = 300,
) {
  let lastExec = 0
  const timer = ref<ReturnType<typeof setTimeout> | null>(null)

  const throttled = (...args: T) => {
    const now = Date.now()
    const remaining = interval - (now - lastExec)
    if (remaining <= 0) {
      if (timer.value) {
        clearTimeout(timer.value)
        timer.value = null
      }
      lastExec = now
      fn(...args)
    } else if (!timer.value) {
      timer.value = setTimeout(() => {
        lastExec = Date.now()
        timer.value = null
        fn(...args)
      }, remaining)
    }
  }

  const cancel = () => {
    if (timer.value) {
      clearTimeout(timer.value)
      timer.value = null
    }
    lastExec = 0
  }

  return { throttled, cancel }
}
